import { STATIC_TALENT_NAVIGATION } from '../components/Layout/talentNavigationConfig.js';

const asPermissionTuple = (value) => Array.isArray(value) && value.length === 3 ? value : null;

export function hasNavigationPermission(permissions = [], requiredPermission) {
  const required = asPermissionTuple(requiredPermission);
  if (!required) return true;
  const [service, resource, action] = required;

  return permissions.some((permission) => {
    if (!permission) return false;
    const permissionService = permission.service;
    const permissionResource = permission.resource;
    const permissionAction = permission.action;

    return (
      (permissionService === '*' && permissionResource === '*' && permissionAction === '*')
      || (permissionService === service && permissionResource === '*' && permissionAction === '*')
      || (permissionService === service && permissionResource === resource && permissionAction === '*')
      || (permissionService === service && permissionResource === '*' && permissionAction === action)
      || (permissionService === service && permissionResource === resource && permissionAction === action)
    );
  });
}

function isItemVisible(item, permissions) {
  if (item.visible === false) return false;
  if (item.permission && !hasNavigationPermission(permissions, item.permission)) return false;
  if (item.anyPermissions?.length) {
    return item.anyPermissions.some((required) => hasNavigationPermission(permissions, required));
  }
  return true;
}

export function pruneNavigation(items = [], permissions = []) {
  return items.flatMap((item) => {
    if (!isItemVisible(item, permissions)) return [];

    const children = pruneNavigation(item.children || [], permissions);
    if (!item.path && item.children && children.length === 0) return [];

    const projected = { ...item };
    if (item.children) projected.children = children;
    return [projected];
  });
}

function normalizeStageOrder(stage) {
  const order = Number(stage?.stage_order);
  return Number.isFinite(order) ? order : 0;
}

const RESERVED_CANDIDATE_STAGE_KEYS = new Set(['rejected']);

function createCandidateStageItems(candidateStages) {
  const seenKeys = new Set();
  return [...(Array.isArray(candidateStages) ? candidateStages : [])]
    .filter((stage) => (
      typeof stage?.key === 'string'
      && stage.key.length > 0
      && !RESERVED_CANDIDATE_STAGE_KEYS.has(stage.key)
    ))
    .sort((left, right) => normalizeStageOrder(left) - normalizeStageOrder(right))
    .flatMap((stage) => {
      if (seenKeys.has(stage.key)) return [];
      seenKeys.add(stage.key);
      return [{
        id: `candidate-stage-${stage.key}`,
        label: stage.name || stage.key,
        path: `/candidates/${stage.key}`,
        icon: `stage:${stage.key}`,
        dynamicCandidateStage: true,
      }];
    });
}

export function augmentCandidateStages(items = [], candidateStages = []) {
  const stageItems = createCandidateStageItems(candidateStages);
  return items.map((item) => {
    const clone = { ...item };
    if (!item.children) return clone;

    const children = augmentCandidateStages(item.children, candidateStages);
    if (item.candidateStagesSlot) {
      const rejectedIndex = children.findIndex((child) => child.id === 'candidates-rejected');
      const insertionIndex = rejectedIndex === -1 ? children.length : rejectedIndex;
      clone.children = [
        ...children.slice(0, insertionIndex),
        ...stageItems,
        ...children.slice(insertionIndex),
      ];
    } else {
      clone.children = children;
    }
    return clone;
  });
}

function toPublicNavItems(
  items,
  iconProjector,
  countsByItemId = {},
  accessibleCountLabelsByItemId = {},
) {
  return items.map((item) => {
    const projected = { id: item.id, label: item.label };
    if (item.path) projected.path = item.path;
    if (item.external) projected.external = true;
    if (item.variant) projected.variant = item.variant;
    if (item.icon && iconProjector) {
      projected.icon = iconProjector(item.icon, item, {
        notificationCount: countsByItemId[item.id],
        notificationCountLabel: accessibleCountLabelsByItemId[item.id],
      });
    }
    if (item.badge !== undefined) projected.badge = item.badge;
    if (item.children?.length) {
      projected.children = toPublicNavItems(
        item.children,
        iconProjector,
        countsByItemId,
        accessibleCountLabelsByItemId,
      );
    }
    return projected;
  });
}

function normalizeCount(count) {
  const numericCount = Number(count);
  return Number.isFinite(numericCount) && numericCount > 0 ? numericCount : 0;
}

export function formatNotificationBadge(count) {
  const normalized = normalizeCount(count);
  if (!normalized) return undefined;
  return normalized > 99 ? '99+' : normalized;
}

export function getNotificationCountLabel(count) {
  const normalized = normalizeCount(count);
  if (!normalized) return undefined;
  return `${normalized} unread notification${normalized === 1 ? '' : 's'}`;
}

function collectSubtreePaths(item, rootItem, paths) {
  if (item !== rootItem && item.path === '/notifications') return;
  if (item.path) paths.add(item.path);
  for (const child of item.children || []) collectSubtreePaths(child, rootItem, paths);
}

export function aggregateNavigationBadges(items = [], notificationCounts = {}) {
  const countsByItemId = {};
  const accessibleCountLabelsByItemId = {};

  const visit = (item) => {
    const children = (item.children || []).map(visit);
    const itemWithChildren = children.length ? { ...item, children } : { ...item };
    const subtreePaths = new Set();
    collectSubtreePaths(itemWithChildren, itemWithChildren, subtreePaths);
    const count = [...subtreePaths].reduce(
      (sum, path) => sum + normalizeCount(notificationCounts[path]),
      0,
    );

    if (!count) return itemWithChildren;
    countsByItemId[item.id] = count;
    accessibleCountLabelsByItemId[item.id] = getNotificationCountLabel(count);
    return { ...itemWithChildren, badge: formatNotificationBadge(count) };
  };

  return {
    items: items.map(visit),
    countsByItemId,
    accessibleCountLabelsByItemId,
  };
}

export function projectGlobalNavigation({
  permissions = [],
  candidateStages = [],
  notificationCounts = {},
  iconProjector,
} = {}) {
  const augmented = augmentCandidateStages(STATIC_TALENT_NAVIGATION, candidateStages);
  const visible = pruneNavigation(augmented, permissions);
  const aggregated = aggregateNavigationBadges(visible, notificationCounts);
  const publicItems = toPublicNavItems(
    aggregated.items,
    iconProjector,
    aggregated.countsByItemId,
    aggregated.accessibleCountLabelsByItemId,
  );
  return { ...aggregated, items: publicItems };
}

function stripQueryAndHash(pathname) {
  if (typeof pathname !== 'string' || pathname.length === 0) return '/';
  const normalized = pathname.split(/[?#]/, 1)[0] || '/';
  return normalized.length > 1 ? normalized.replace(/\/+$/, '') : normalized;
}

function candidateStageKeys(candidateStages) {
  return new Set((Array.isArray(candidateStages) ? candidateStages : []).flatMap((stage) => {
    if (typeof stage === 'string') return [stage];
    return typeof stage?.key === 'string' ? [stage.key] : [];
  }));
}

export function resolveTalentActivePath(pathname, candidateStages = []) {
  const path = stripQueryAndHash(pathname);

  if (path === '/statistics') return '/statistics/dashboard';
  if (path === '/statuses/order-management') return '/statuses';
  if (path === '/candidates/rejected') return path;

  const candidateMatch = path.match(/^\/candidates\/([^/]+)$/);
  if (candidateMatch) {
    return candidateStageKeys(candidateStages).has(candidateMatch[1])
      ? `/candidates/${candidateMatch[1]}`
      : '/candidates';
  }

  const routeFamilies = [
    '/talents',
    '/job-applications',
    '/talent-templates',
    '/talent-posts',
    '/prompts',
  ];
  for (const basePath of routeFamilies) {
    if (path !== basePath && path.startsWith(`${basePath}/`)) return basePath;
  }

  return path;
}

function isDetailSectionVisible(section, hasPermission, talentType) {
  if (section.requiresPermission) return hasPermission(...section.requiresPermission);
  if (section.permission) return hasPermission(...section.permission);
  if (section.subResources?.length) {
    if (!talentType) return false;
    return section.subResources.some((resource) => hasPermission(
      'talent-service',
      `${talentType}_${resource}`,
      section.navAction || 'read',
    ));
  }
  if (section.subResource) {
    if (!talentType) return false;
    return hasPermission(
      'talent-service',
      `${talentType}_${section.subResource}`,
      section.navAction || 'read',
    );
  }
  return true;
}

export function filterTalentDetailSections({
  sections = [],
  talentType,
  hasPermission = () => false,
} = {}) {
  return sections.filter((section) => isDetailSectionVisible(section, hasPermission, talentType));
}

const TALENT_DETAIL_BACK_ICON = 'ChevronLeft';

export function getTalentDetailBackLabel(backPath) {
  const path = stripQueryAndHash(backPath || '/talents');
  if (path.startsWith('/candidates')) return 'Back to Candidates';
  if (path.startsWith('/trainees')) return 'Back to Trainees';
  if (path.startsWith('/employees')) return 'Back to Employees';
  if (path.startsWith('/job-applications')) return 'Back to Job Applications';
  if (path === '/') return 'Back to Dashboard';
  if (path.startsWith('/calendar')) return 'Back to Calendar';
  if (path.startsWith('/talent-posts')) return 'Back to Talent Posts';
  if (path.startsWith('/talents')) return 'Back to Talents';
  return 'Back';
}

export function projectPublishedTalentDetailNavigation({
  sidebarNav,
  backPath,
  iconProjector,
} = {}) {
  if (
    !Array.isArray(sidebarNav?.sections)
    || sidebarNav.sections.length === 0
    || typeof sidebarNav.onItemClick !== 'function'
  ) return null;

  const seenIds = new Set();
  const visibleSections = sidebarNav.sections.filter((section) => {
    if (typeof section?.id !== 'string' || typeof section?.label !== 'string') return false;
    if (seenIds.has(section.id)) return false;
    seenIds.add(section.id);
    return true;
  });
  if (visibleSections.length === 0) return null;

  const resolvedBackPath = backPath || '/talents';
  const backItem = {
    id: 'talent-detail-back',
    label: getTalentDetailBackLabel(resolvedBackPath),
    path: resolvedBackPath,
  };
  if (iconProjector) backItem.icon = iconProjector(TALENT_DETAIL_BACK_ICON, backItem);
  const items = [backItem, ...visibleSections.map((section) => {
    const item = { id: section.id, label: section.label };
    if (section.icon && iconProjector) item.icon = iconProjector(section.icon, section);
    return item;
  })];
  const visibleIds = new Set(visibleSections.map((section) => section.id));
  const fallbackActiveId = visibleIds.has('overview') ? 'overview' : visibleSections[0].id;

  return {
    items,
    activeItemId: visibleIds.has(sidebarNav.activeId) ? sidebarNav.activeId : fallbackActiveId,
    visibleSections,
    onItemClick: sidebarNav.onItemClick,
  };
}

export function projectTalentDetailNavigation({
  sections = [],
  permissions = [],
  talentType,
  backPath,
  activeSectionId,
  iconProjector,
} = {}) {
  const visibleSections = filterTalentDetailSections({
    sections,
    talentType,
    hasPermission: (...tuple) => hasNavigationPermission(permissions, tuple),
  });
  const sectionItems = visibleSections.map((section) => {
    const item = { id: section.id, label: section.label };
    if (section.icon && iconProjector) item.icon = iconProjector(section.icon, section);
    return item;
  });
  const resolvedBackPath = backPath || '/talents';
  const backItem = {
    id: 'talent-detail-back',
    label: getTalentDetailBackLabel(resolvedBackPath),
    path: resolvedBackPath,
  };
  if (iconProjector) backItem.icon = iconProjector(TALENT_DETAIL_BACK_ICON, backItem);
  const visibleIds = new Set(visibleSections.map((section) => section.id));
  const fallbackActiveId = visibleIds.has('overview') ? 'overview' : visibleSections[0]?.id;

  return {
    items: [backItem, ...sectionItems],
    activeItemId: visibleIds.has(activeSectionId) ? activeSectionId : fallbackActiveId,
    visibleSections,
  };
}
